import { fetchData, API_TYPE, IResponse } from '@umu/umu-fetch';
import { safeJSONParse } from 'businessCommon/src/@utils/jsonParse';
import EventEmitter from 'events';
import { RENDER_TYPE, RENDER_HIT_TYPE, IPdfImages } from '../types/index';
import { isIos, isSafari } from '@umu/umu-ua';
import { getPixelRatio } from '../utils/common';

const Api = {
    /**
     * 使用Apollo的配置，获取对应pdf url指定的类型
     */
    GET_RENDER_TYPE: {
        url: `/napi/config/center`,
        type: API_TYPE.GET,
    },
};

/**
 * @see https://app.asana.com/0/357313196454880/1204063174133986/f
 * @param key apollo key
 * @returns
 */
class ConfigRenderHelper extends EventEmitter {
    /**
     * 企业id的key
     */
    static ENTERPRISE_ID = 'enterpriseId';
    /**
     * 小节id
     */
    static ELEMENT_ID = 'elementId';

    /**
     * 企业id的key
     */
    static PIXEL_ENTERPRISE_ID = 'pixelEnterpriseId';
    /**
     * 小节id
     */
    static PIXEL_ELEMENT_ID = 'pixelElementId';

    /**
     * pdf url
     */
    static PDF_URL = 'pdfUrl';

    /**
     * pdf url
     */
    static PIXEL_PDF_URL = 'pixelPdfUrl';
    /**
     * 当前所有配置
     */
    static RENDER_TYPE_CONFIG: { [key: string]: 'svg' } | null = null;
    /**
     * 最大等待时间
     */
    static EVENT_CONFIG_LOADED = 'event_pdf_render_type_config_loaded';

    isLoaded: boolean = false;

    /**
     * 渲染类型命中类型
     */
    hitType: RENDER_HIT_TYPE = RENDER_HIT_TYPE.NONE;

    public getRenderType = (pdfUrl: string, elementId?: string, enterpriseId?: string, pdfImages?: IPdfImages) => {
        if (this.isEnableImageRender(pdfImages)) {
            return RENDER_TYPE.IMAGE;
        } else if (this.isEnableSvgRender(pdfUrl, elementId, enterpriseId)) {
            return RENDER_TYPE.SVG;
        }
        return RENDER_TYPE.CANVAS;
    };

    public getRenderPixelRadio = (pdfUrl: string, elementId?: string, enterpriseId?: string) => {
        if (this.isEnablePixelRender(pdfUrl, elementId, enterpriseId)) {
            return 2;
        }
        return getPixelRatio();
    };

    public async loadConfig() {
        if (this.isLoaded) {
            return;
        }
        const key = 'session_document_pdf_render_type';
        const params = {
            key,
        };
        let res: IResponse<string> | undefined;
        try {
            res = await fetchData(Api.GET_RENDER_TYPE, params);
            ConfigRenderHelper.RENDER_TYPE_CONFIG = safeJSONParse(res?.data || '{}', {});
            this.isLoaded = true;
        } catch (error) {
            this.isLoaded = true;
            ConfigRenderHelper.RENDER_TYPE_CONFIG = {}; // 失败时使用空配置
        } finally {
            this.emit(ConfigRenderHelper.EVENT_CONFIG_LOADED, {
                isLoaded: this.isLoaded,
            });
        }
    }

    /**
     * 是否加载完成
     * @param pdfUrl
     */
    onConfigLoaded(cb: (isLoaded: boolean) => void) {
        this.on(ConfigRenderHelper.EVENT_CONFIG_LOADED, (params: { isLoaded: boolean }) => {
            cb(params.isLoaded);
        });
    }

    isEnableImageRender(pdfImages?: IPdfImages) {
        // 只有在“确实有可用的转码图片列表”时才启用 IMAGE。
        // 否则会误判为 IMAGE，导致正文不走 pdf.js 渲染，出现空白页（尤其在 Safari 下更难排查）。
        // 经验：Safari/iOS 下图片加载偶发长期 pending（即使是 png），会导致页面长时间空白。
        // 为保证稳定性，Safari/iOS 直接禁用 IMAGE，走 CANVAS。
        if (isIos || isSafari) {
            return false;
        }
        return !!pdfImages?.path && Array.isArray(pdfImages.list) && pdfImages.list.length > 0;
    }

    /**
     * 是否允许pdf使用svg
     * @param pdfUrl
     * @returns
     */
    isEnableSvgRender(resourceUrl?: string, elementId?: string, enterpriseId?: string) {
        if (isIos || isSafari) {
            return false;
        }
        if (!ConfigRenderHelper.RENDER_TYPE_CONFIG) {
            return false;
        }

        // 小节id命中白名单
        if (this.isEnableSvgRenderInElement(elementId)) {
            this.hitType = RENDER_HIT_TYPE.ELEMENT_ID;
            return true;
        }

        // pdf url命中
        if (this.isEnableSvgRenderByPdfUrl(resourceUrl)) {
            this.hitType = RENDER_HIT_TYPE.PDF_URL;
            return true;
        }

        // 企业id命中白名单
        if (this.isEnableSvgRenderInEnterprise(enterpriseId)) {
            this.hitType = RENDER_HIT_TYPE.ENTERPRISE_ID;
            return true;
        }

        return false;
    }

    /**
     * 是否允许 pdf 使用 canvas 渲染时提升 pixelRatio
     * @param pdfUrl
     * @returns
     */
    isEnablePixelRender(resourceUrl?: string, elementId?: string, enterpriseId?: string) {
        if (!ConfigRenderHelper.RENDER_TYPE_CONFIG) {
            return false;
        }

        // 小节id命中白名单
        if (this.isEnablePixelRenderInElement(elementId)) {
            this.hitType = RENDER_HIT_TYPE.ELEMENT_ID;
            return true;
        }

        // pdf url命中
        if (this.isEnablePixelRenderByPdfUrl(resourceUrl)) {
            this.hitType = RENDER_HIT_TYPE.PDF_URL;
            return true;
        }

        // 企业id命中白名单
        if (this.isEnablePixelRenderInEnterprise(enterpriseId)) {
            this.hitType = RENDER_HIT_TYPE.ENTERPRISE_ID;
            return true;
        }

        return false;
    }

    private isEnableSvgRenderInEnterprise(enterpriseId?: string) {
        if (!enterpriseId || !ConfigRenderHelper.RENDER_TYPE_CONFIG) {
            return false;
        }
        const list = ConfigRenderHelper.RENDER_TYPE_CONFIG[ConfigRenderHelper.ENTERPRISE_ID] || '';
        return this.isIdInList(enterpriseId, list);
    }

    private isEnableSvgRenderInElement(elementId?: string) {
        if (!elementId || !ConfigRenderHelper.RENDER_TYPE_CONFIG) {
            return false;
        }
        const list = ConfigRenderHelper.RENDER_TYPE_CONFIG[ConfigRenderHelper.ELEMENT_ID] || '';
        return this.isIdInList(elementId, list);
    }

    private isEnablePixelRenderInEnterprise(enterpriseId?: string) {
        if (!enterpriseId || !ConfigRenderHelper.RENDER_TYPE_CONFIG) {
            return false;
        }
        // 按照设计规范，像素比提升使用独立的 pixelEnterpriseId 配置字段
        // 与 SVG 渲染的 enterpriseId 配置完全独立，不复用
        const list = ConfigRenderHelper.RENDER_TYPE_CONFIG[ConfigRenderHelper.PIXEL_ENTERPRISE_ID] || '';
        return this.isIdInList(enterpriseId, list);
    }

    private isEnablePixelRenderInElement(elementId?: string) {
        if (!elementId || !ConfigRenderHelper.RENDER_TYPE_CONFIG) {
            return false;
        }
        const list = ConfigRenderHelper.RENDER_TYPE_CONFIG[ConfigRenderHelper.PIXEL_ELEMENT_ID] || '';
        return this.isIdInList(elementId, list);
    }

    private isEnableSvgRenderByPdfUrl(resourceUrl?: string) {
        if (!resourceUrl || !ConfigRenderHelper.RENDER_TYPE_CONFIG) {
            return false;
        }
        const list = ConfigRenderHelper.RENDER_TYPE_CONFIG[ConfigRenderHelper.PDF_URL] || '';
        return this.isIdInList(resourceUrl, list);
    }

    private isEnablePixelRenderByPdfUrl(resourceUrl?: string) {
        if (!resourceUrl || !ConfigRenderHelper.RENDER_TYPE_CONFIG) {
            return false;
        }
        const list = ConfigRenderHelper.RENDER_TYPE_CONFIG[ConfigRenderHelper.PIXEL_PDF_URL] || '';
        return this.isIdInList(resourceUrl, list);
    }

    /**
     *
     * @param id 需要查找的id
     * @param ids "1,2,3,4,5"
     * @returns
     */
    isIdInList(id: string, ids: string = '') {
        const list = ids.split(',');
        return (
            list.filter((element) => {
                return element.trim() === (id + '').trim();
            }).length > 0
        );
    }
}

const configRenderHelper = new ConfigRenderHelper();

export { configRenderHelper };
